import { useEffect, useState } from 'react';
import { Modal, Spinner } from './ui.jsx';
import { t } from '../i18n/index.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

function extOf(name = '') {
  return name.split('.').pop().toLowerCase();
}

/**
 * In-app previewer. Fetches the auth-protected file as a blob, then renders:
 *  - pdf  -> embedded iframe
 *  - xlsx/xls/csv -> SheetJS table (lazy-loaded)
 *  - docx -> mammoth HTML (lazy-loaded)
 *  - else -> download link
 *
 * `path` is the API path, e.g. /files/12/download or /contract-files/3/download.
 */
export default function FileViewer({ path, name, onClose }) {
  const [state, setState] = useState({ loading: true });
  const ext = extOf(name);
  const downloadUrl = `${API_BASE}${path}`;

  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const res = await fetch(downloadUrl, { credentials: 'include' });
        if (!res.ok) throw new Error(`Could not load file (${res.status})`);
        const buf = await res.arrayBuffer();

        if (ext === 'pdf') {
          const url = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
          revoke = url;
          setState({ loading: false, kind: 'pdf', url });
        } else if (['xlsx', 'xls', 'csv'].includes(ext)) {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(buf, { type: 'array' });
          const sheets = wb.SheetNames.map((n) => ({ name: n, html: XLSX.utils.sheet_to_html(wb.Sheets[n]) }));
          setState({ loading: false, kind: 'sheet', sheets, active: 0 });
        } else if (ext === 'docx') {
          const mammoth = await import('mammoth/mammoth.browser.js');
          const { value } = await mammoth.convertToHtml({ arrayBuffer: buf });
          setState({ loading: false, kind: 'doc', html: value });
        } else if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
          const url = URL.createObjectURL(new Blob([buf]));
          revoke = url;
          setState({ loading: false, kind: 'image', url });
        } else {
          setState({ loading: false, kind: 'other' });
        }
      } catch (e) {
        setState({ loading: false, error: e.message });
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [downloadUrl, ext]);

  return (
    <Modal title={name} onClose={onClose}
      footer={<div className="modal-actions"><div className="spacer" /><a className="btn ghost" href={downloadUrl}>{t('Download')}</a><button className="btn primary" onClick={onClose}>{t('Close')}</button></div>}>
      <div className="viewer">
        {state.loading && <Spinner />}
        {state.error && <div className="alert error">{state.error}</div>}
        {state.kind === 'pdf' && <iframe title={name} src={state.url} className="viewer-pdf" />}
        {state.kind === 'image' && <img src={state.url} alt={name} className="viewer-img" />}
        {state.kind === 'doc' && <div className="viewer-doc" dangerouslySetInnerHTML={{ __html: state.html }} />}
        {state.kind === 'sheet' && (
          <div>
            {state.sheets.length > 1 && (
              <div className="sheet-tabs">
                {state.sheets.map((s, i) => (
                  <button key={s.name} className={`chip ${i === state.active ? 'on' : ''}`} onClick={() => setState({ ...state, active: i })}>{s.name}</button>
                ))}
              </div>
            )}
            <div className="viewer-sheet" dangerouslySetInnerHTML={{ __html: state.sheets[state.active].html }} />
          </div>
        )}
        {state.kind === 'other' && (
          <p className="muted">
            {t('No inline preview for “.{ext}” files. Use Download.', { ext })}
            {ext === 'doc' && ` ${t('(Tip: convert this older format to .docx to preview it directly in the app.)')}`}
          </p>
        )}
      </div>
    </Modal>
  );
}
